import { omitBy, isUndefined, chunk, isArray } from 'lodash'
import { Auth } from '@aws-amplify/auth'

class FetchError extends Error {
  constructor(obj) {
    super()
    this.detail = obj.detail
    this.code = obj.code
    this.status = obj.status
    this.json = obj.json
  }
}

export const waitingState = (key, state) => {
  return {
    type: 'WAITING',
    payload: {
      key, state: !!state
    }
  }
}

export const getActionSuccessKey = (key, id) => {
  return `${key}_${id}`
}

export const getActionErrorKey = (key, id) => {
  return `${key}_${id}_ERROR`
}

export const getDispatch = (request, successKey, errorKey, id, params, clearKey) => async (dispatch) => {
  try {
    dispatch(waitingState(successKey, true))
    if (clearKey) await dispatch({ type: clearKey })

    const data = await request

    return dispatch({
      type: successKey,
      payload: id ? { id, data } : data,
      ...params
    })
  } catch (e) {
    return dispatch({
      type: errorKey,
      payload: e
    })
  } finally {
    dispatch(waitingState(successKey, false))
  }
}

export const fetchJSON = async function () {
  const args = Array.from(arguments)

  const res = await global.fetch.apply(this, args)
  try {
    let json = {}
    if (res.status !== 204 && res.status !== 404) {
      json = await res.json()
    }
    if (!res.ok) {
      throw new FetchError({ json, status: res.status })
    }
    return { json, status: res.status }
  } catch (error) {
    throw new FetchError({ ...error })
  }
}

export const apiFetch = async function (path, ...rest) {
  const args = {
    method: 'GET',
    headers: new global.Headers(),
    ...rest[0]
  }

  // Set content type it none supplied
  if (!args.headers.get('Content-Type')) {
    args.headers.set('Content-Type', 'application/json')
  }

  // Set authorization if cognito token exists and none supplied
  try {
    const { accessToken } = await Auth.currentSession()
    if (accessToken && !args.headers.get('Authorization')) {
      args.headers.set('Authorization', `Bearer ${accessToken.jwtToken}`)
    }
  } catch (e) {
    // TODO: handleErrors - log out or refreshSession when user does not exist
    console.log('Amplify error: ', e)
  }

  const url = process.env.CONFIG.API_URL + path
  const fetch = () => fetchJSON.apply(this, [url, { ...args }])

  try {
    const { json: result } = await fetch()
    return result
  } catch (error) {
    if (error.status === 502 || error.status === 503) {
      const { json: result } = await fetch()
      return result
    }
    throw error
  }
}

export const omitEmpty = function (data) {
  return omitBy(data, val => isUndefined(val) || val === null)
}

export const apiBatchFetch = async function (path, params, batchSize = 100) {
  const args = {
    method: 'GET',
    headers: new global.Headers(),
    ...params
  }

  // Set content type it none supplied
  if (!args.headers.get('Content-Type')) {
    args.headers.set('Content-Type', 'application/json')
  }

  // Set authorization if cognito token exists and none supplied
  try {
    const { accessToken } = await Auth.currentSession()
    if (accessToken && !args.headers.get('Authorization')) {
      args.headers.set('Authorization', `Bearer ${accessToken.jwtToken}`)
    }
  } catch (e) {
    console.log('Amplify error: ', e)
  }

  const url = process.env.CONFIG.API_URL + path

  const batchPayloads = (body) => {
    if (!params.body) return [undefined]
    const payloads = JSON.parse(body)
    return isArray(payloads) ? chunk(payloads, batchSize) : [payloads]
  }
  const payloadBatches = batchPayloads(params?.body)

  const results = await Promise.all(payloadBatches.map(async payload => {
    const { json: result } = await fetchJSON.apply(this, [url, {
      ...args,
      ...(payload && { body: JSON.stringify(payload) })
    }])
    return result.data ? result.data : result
  }))
  return { data: results }
}
